import { pushDataLayer } from './dataLayer';
import {
  getBannerClickEvent,
  getBannerShowEvent,
  getCalendarDayClickEvent,
  getFilterInteractionEvent,
  getFiltersAppliedEvent,
  getFiltersOpenEvent,
  getFilterToggleEvent,
  getSearchClickEvent,
  getSearchCountEvent,
  getSearchCriteriaEvents,
  getSearchEvent,
  getSideBarFilterEvent,
  getVariableEvent,
} from './dataLayerEvents';
import { raiseDataLayerMaxStep, startDataLayerSession } from './dataLayerSession';
import {
  DataLayerEventName,
  type DataLayerFilterInteraction,
  type DataLayerSearchClick,
  type DataLayerSearchCriteria,
} from '../model/dataLayerEvents';
import type { DataLayerEvent } from '../model/types';

function pushDataLayerEvents(events: readonly DataLayerEvent[]): boolean {
  return events.map((event) => pushDataLayer(event)).every(Boolean);
}

export default class DataLayerTracker {
  toggleFilter(checked: boolean, value: string): boolean {
    return pushDataLayer(getFilterToggleEvent(checked, value));
  }

  sideBarFilter(beach: string): boolean {
    return pushDataLayer(getSideBarFilterEvent(beach));
  }

  openFilters(): boolean {
    return pushDataLayer(getFiltersOpenEvent());
  }

  filterInteraction(interaction: DataLayerFilterInteraction, filterPage: string): boolean {
    return pushDataLayer(getFilterInteractionEvent(interaction, filterPage));
  }

  filtersApplied(isExclusive: boolean, filterPage: string): boolean {
    return pushDataLayer(getFiltersAppliedEvent(isExclusive, filterPage));
  }

  searchCount(count: number): boolean {
    return pushDataLayer(getSearchCountEvent(count));
  }

  search(hotelIds: readonly number[], bannersStep: number): boolean {
    return pushDataLayer(getSearchEvent(hotelIds, bannersStep));
  }

  searchClick(click: DataLayerSearchClick): boolean {
    return pushDataLayer(getSearchClickEvent(click));
  }

  calendarDayClick(hasPrice: boolean): boolean {
    return pushDataLayer(getCalendarDayClickEvent(hasPrice));
  }

  searchPage(step: string, maxStep: number): boolean {
    if (typeof window === 'undefined') return false;

    try {
      const now = new Date();
      const start = startDataLayerSession(now);
      const isMaxStepRaised = raiseDataLayerMaxStep(maxStep);

      return pushDataLayerEvents([
        getVariableEvent(DataLayerEventName.TabId, String(now.getTime())),
        getVariableEvent(DataLayerEventName.Step, step),
        ...(start ? [getVariableEvent(DataLayerEventName.Start, start)] : []),
        ...(isMaxStepRaised ? [getVariableEvent(DataLayerEventName.MaxStep, maxStep)] : []),
        getVariableEvent(DataLayerEventName.CacheResults, true),
        getVariableEvent(DataLayerEventName.PreSearchPagePath, window.location.pathname),
      ]);
    } catch {
      return false;
    }
  }

  searchCriteria(sid: string, criteria: DataLayerSearchCriteria): boolean {
    return pushDataLayerEvents(getSearchCriteriaEvents(sid, criteria));
  }

  bannerShow(value: string): boolean {
    return pushDataLayer(getBannerShowEvent(value));
  }

  bannerClick(value: string): boolean {
    return pushDataLayer(getBannerClickEvent(value));
  }
}

const dataLayerTracker = new DataLayerTracker();

export { dataLayerTracker };
